import type { Product, SmmProductConfig } from '#bv/data/products'
import {
  INSTAGRAM_REAL_FOLLOWERS_PRODUCT_ID,
  TIKTOK_REAL_FOLLOWERS_PRODUCT_ID,
  YOUTUBE_SUBSCRIBERS_PRODUCT_ID,
  productsShownInShop,
} from '#bv/data/products'
import { allSmmPackages } from '#bv/lib/smmTiers'

export type { SmmPackage, SmmPlatform, SmmProductConfig, SmmTrustBadge } from '#bv/data/products'
export {
  INSTAGRAM_REAL_FOLLOWERS_PRODUCT_ID,
  TIKTOK_REAL_FOLLOWERS_PRODUCT_ID,
  YOUTUBE_SUBSCRIBERS_PRODUCT_ID,
}

export const SMM_PRODUCT_IDS = [
  TIKTOK_REAL_FOLLOWERS_PRODUCT_ID,
  INSTAGRAM_REAL_FOLLOWERS_PRODUCT_ID,
  YOUTUBE_SUBSCRIBERS_PRODUCT_ID,
] as const

export function isKnownSmmProductId(productId: number): boolean {
  return (SMM_PRODUCT_IDS as readonly number[]).includes(productId)
}

export function isSmmProduct(p: Product | null | undefined): p is Product & { smmConfig: SmmProductConfig } {
  return Boolean(p?.smmConfig && allSmmPackages(p.smmConfig).length > 0)
}

export function listSmmProducts(): Array<Product & { smmConfig: SmmProductConfig }> {
  return productsShownInShop().filter(isSmmProduct)
}

/** Cart / ledger field — still named smmTiktokTarget for storage compatibility. */
export function smmTargetFromCartLine(line: { smmTiktokTarget?: string; smmTarget?: string }): string {
  return (line.smmTarget ?? line.smmTiktokTarget ?? '').trim()
}

export type SmmCheckoutMeta = {
  productId: number
  packageQuantity: number
  tiktokTarget: string
  serviceId: number
  tierId?: string
}

export function smmCheckoutMetaFromRecord(meta: Record<string, string>): SmmCheckoutMeta | null {
  if (meta.smm !== '1') return null
  const productId = Number(meta.productId)
  const packageQuantity = Number(meta.smmPackageQuantity)
  const tiktokTarget = (meta.smmTiktokTarget ?? meta.smmTarget ?? '').trim()
  const serviceId = Number(meta.smmServiceId)
  const tierId = (meta.smmTierId ?? meta.tierId ?? '').trim() || undefined
  if (!Number.isInteger(productId) || productId <= 0) return null
  if (!Number.isInteger(packageQuantity) || packageQuantity <= 0) return null
  if (!tiktokTarget) return null
  if (!Number.isInteger(serviceId) || serviceId <= 0) return null
  return { productId, packageQuantity, tiktokTarget, serviceId, tierId }
}

export function smmMetadataForCheckout(meta: SmmCheckoutMeta): Record<string, string> {
  return {
    smm: '1',
    productId: String(meta.productId),
    smmPackageQuantity: String(meta.packageQuantity),
    smmTiktokTarget: meta.tiktokTarget,
    smmServiceId: String(meta.serviceId),
    ...(meta.tierId ? { smmTierId: meta.tierId } : {}),
  }
}
